import { notificationConstants } from '../_constants/notification.constants';
import {
  deriveNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from '../_services/notifications.service';
import { getNotificationRole } from '../component/notifications/role';

export const notificationsActions = {
  loadNotifications,
  loadUnreadCount,
  markViewed,
  markAllRead,
  markOneRead,
};

/**
 * Fetch the full notification list. Used on bell open + on initial mount.
 * Heavier query than the count poll — only call when actually needed for
 * rendering the panel.
 */
function loadNotifications() {
  return async (dispatch, getState) => {
    const state = getState();
    const user = state?.user || null;
    const role = getNotificationRole(user);
    const currencySymbol = user?.userInfo?.hairdresser?.currencySymbol || '£';
    dispatch({ type: notificationConstants.LOAD_REQUEST });
    try {
      const items = await deriveNotifications({ user, role, currencySymbol });
      dispatch({ type: notificationConstants.LOAD_SUCCESS, items });
      return items;
    } catch (error) {
      dispatch({ type: notificationConstants.LOAD_FAILURE, error: error?.message || 'Failed to load' });
      return [];
    }
  };
}

/**
 * Cheap routine poll. Hits /notification-v2/unread-count which is an
 * indexed countDocuments — sub-1ms typical. The bell badge re-renders
 * from this; the full list only fetches when the user opens the panel
 * or on initial mount. Order of magnitude less load than polling the
 * full list every minute.
 */
function loadUnreadCount() {
  return async (dispatch) => {
    try {
      const count = await fetchUnreadCount();
      // A request that failed leaves the badge as it was
      if (count === null) return null;
      dispatch({ type: notificationConstants.UNREAD_COUNT_SUCCESS, count });
      return count;
    } catch (error) {
      // Silent — count poll failure shouldn't surface or retry
      // aggressively. Hook layer handles backoff.
      return 0;
    }
  };
}

function markViewed(timestamp) {
  return { type: notificationConstants.MARK_VIEWED, timestamp: timestamp || Date.now() };
}

/**
 * Mark every notification read — badge included.
 *
 * "Mark all read" used to dispatch markViewed() alone, which only moves the
 * local lastViewedAt. The badge reads the SERVER count (refreshed by the
 * /unread-count poll), so the server never learned anything and the next poll
 * restored the old number: the badge appeared stuck. markAllNotificationsRead
 * existed in the service layer but nothing ever called it.
 *
 * Clear locally first so the tap feels instant, then persist, then reconcile
 * with the server's own count so we never drift from the truth.
 */
function markAllRead() {
  return async (dispatch) => {
    dispatch({ type: notificationConstants.MARK_VIEWED, timestamp: Date.now() });
    dispatch({ type: notificationConstants.MARK_ALL_READ });
    await markAllNotificationsRead();
    return dispatch(loadUnreadCount());
  };
}

/**
 * Mark ONE notification read (tapping it in the panel).
 *
 * Guarded on isServerRow: only server-backed rows carry a real notification
 * _id. Legacy client-derived rows use synthetic ids ('booking:<id>:created')
 * which would 4xx against the per-notification endpoint — those still fall
 * back to the local lastViewedAt behaviour, which is all they ever had.
 */
function markOneRead(item) {
  return async (dispatch) => {
    if (!item || !item.isServerRow || !item.id) return null;
    dispatch({ type: notificationConstants.MARK_ONE_READ, id: item.id });
    await markNotificationRead(item.id);
    return dispatch(loadUnreadCount());
  };
}
